import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import without from 'lodash/without';

import getUpcomingEvents, {
    type GetUpcomingEventsParams,
} from 'api/events/get-upcoming-events';
import {
    type GetEventTypeOptionsRequest,
    type UpcomingEvent,
} from 'api/events/events.types';
import getEventTypeOptions from 'api/events/get-event-type-options';
import getEventVenues from 'api/events/get-event-venues';
import {initialState} from 'pages/comp-pass/store/comp-pass-slice';
import formatEventFilterOptions from 'pages/event-management/utils/format-event-type-options';
import dayjs from 'utils/dayjs';

export interface EventFiltersPayload {
    eventTypeIds?: number[];
    destinationIds?: number[];
    toDate?: string;
    fromDate?: string;
}

/**
 * Action to update the selected events for the comp pass creation.
 */
export const updateEventSelection = createAction<UpcomingEvent[]>(
    'compPass/updateEventSelection'
);

/**
 * A Redux thunk for fetching event filter options.
 */
export const getEventFilterOptions = createAsyncThunk(
    'compPass/getEventFilterOptions',
    async (
        {facilityId, fromDate, toDate}: GetEventTypeOptionsRequest,
        {rejectWithValue}
    ) => {
        try {
            const [eventTypeResponse, venueResponse] = await Promise.all([
                getEventTypeOptions({
                    facilityId,
                    fromDate: dayjs(fromDate),
                    toDate: dayjs(toDate),
                }),
                getEventVenues(facilityId),
            ]);

            const formattedEventTypes =
                formatEventFilterOptions(eventTypeResponse);
            const formattedVenues = formatEventFilterOptions(venueResponse);

            return {
                eventTypeIds: formattedEventTypes,
                venueIds: formattedVenues,
            };
        } catch (error) {
            console.error('getEventFilterOptions error:', {error});

            return rejectWithValue(error);
        }
    }
);

/**
 * Action to update filters with the count of applied filters
 *
 * Action Type: eventSchedule/updateFilters
 */
export const updateEventFilters = createAction(
    'compPass/updateEventFilters',
    (payload: EventFiltersPayload) => {
        const appliedFilters = without(
            Object.values(payload),
            ...Object.values(initialState.filterOptions)
        );

        let appliedFiltersCount = 0;

        appliedFilters.forEach((filter) => {
            if (filter.length > 0) {
                appliedFiltersCount++;
            }
        });

        return {
            payload: {
                ...payload,
                numFiltersApplied: appliedFiltersCount,
            },
        };
    }
);

/**
 * Thunk to get the upcoming events data
 *
 * Action Type: eventSchedule/getUpcomingEventsData
 *
 */
export const getUpcomingEventsData = createAsyncThunk(
    'compPass/getUpcomingEventsData',
    async (
        {facilityId, requestData, useISOString}: GetUpcomingEventsParams,
        {rejectWithValue, signal}
    ) => {
        try {
            const response = await getUpcomingEvents({
                facilityId,
                requestData,
                useISOString,
                config: {signal},
            });

            const resultData = response.data.results.map((item) => {
                const {tiers} = item;

                item.totalInventory = tiers.reduce(
                    (partialSum, tier) => partialSum + (tier?.inventory || 0),
                    0
                );

                return item;
            });

            return {
                data: resultData,
                meta: response.meta,
            };
        } catch (err) {
            rejectWithValue(err);
        }
    }
);
